import { useEffect, useLayoutEffect, useState } from 'react';
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
/** Shell-owned sessions survive panel navigation, but never their owner or API.
 * Runtime implementations arrive only through the named companion lazy chunk. */
export function useCompanionSessions(enabled, owner, transport, factories, onDisabled) {
    const review = factories.createActionReviewSession, checks = factories.createCheckStatusSessions;
    const [loaded, setLoaded] = useState(null), [failed, setFailed] = useState(null);
    useClientLayoutEffect(() => {
        if (!enabled)
            return;
        let current = true, action, status;
        const scope = { owner, transport, review, checks, disabled: onDisabled };
        const clear = () => { action?.clear(); status?.clear(); };
        const actionReady = Promise.resolve().then(() => current ? review(owner, transport) : undefined).then(value => {
            action = value;
            if (!current)
                value?.clear();
            return value;
        });
        const statusReady = Promise.resolve().then(() => current ? checks(owner, transport, onDisabled) : undefined).then(value => {
            status = value;
            if (!current)
                value?.clear();
            return value;
        });
        void Promise.all([actionReady, statusReady]).then(([a, s]) => {
            if (!current)
                return;
            if (!a || !s || a.owner !== owner || a.transport !== transport || s.owner !== owner)
                throw new Error('Companion session unavailable');
            setLoaded({ ...scope, action: a, status: s });
            setFailed(null);
        }).catch(() => {
            if (!current)
                return;
            current = false;
            clear();
            setLoaded(null);
            setFailed(scope);
        });
        return () => {
            current = false;
            clear();
            setLoaded(previous => previous?.owner === owner && previous.transport === transport
                && previous.review === review && previous.checks === checks && previous.disabled === onDisabled ? null : previous);
        };
    }, [enabled, owner, transport, review, checks, onDisabled]);
    const matches = (value) => enabled && value?.owner === owner && value.transport === transport
        && value.review === review && value.checks === checks && value.disabled === onDisabled;
    return { action: matches(loaded) ? loaded.action : undefined, status: matches(loaded) ? loaded.status : undefined,
        ready: matches(loaded), failed: matches(failed) };
}
//# sourceMappingURL=useCompanionSessions.js.map